import { CiapSelectFieldModel } from 'components/form/field/select/CiapSelectField/CiapSelectField'
import { CidSelectFieldModel } from 'components/form/field/select/CidSelectField/CidSelectField'
import { MedicamentoCatmatSelectModel } from 'components/form/field/select/MedicamentoCatMatSelectField'
import { tiposReceitaRecord } from 'components/form/field/select/TipoReceitaSelectField'
import { ViaAdministracaoSelectModel } from 'components/form/field/select/ViaAdministracaoSelectField'
import {
  DisponibilidadeHorusEnum,
  ListaMedicamentoEnum,
  TipoFrequencia,
  TipoMotivoPrescricaoEnum,
  TipoReceitaEnum,
  UnidadeMedidaTempoEnum,
} from 'graphql/types.generated'
import { turnoByStringRecord } from 'types/enums'
import { instantToYyyyMmDd } from 'util/date/converter'
import { dateAsYyyyMmDd, toDate } from 'util/date/formatDate'
import { parseNumber } from 'util/validation'
import { isEmpty } from 'util/validation/Util'
import { v4 as uuidv4 } from 'uuid'
import { formatCidOrCiap } from 'view/atendimentos/utils/format-atendimento'

import { PrescricaoDigitalTableModel } from '../components/PrescricaoDigitalTable'
import { MedicamentoToPrintDto } from '../modal/impressao/ImpressaoMedicamentosInput'
import { PrescricaoDigitalFormModel } from '../modal/prescricao-digital/model-prescricaoDigital'
import { MedicamentoFormModel, ReceitaMedicamentoQueryModel, TipoDoseEnum } from '../model-prescricao'
import {
  formatDoseReceitaLabel,
  formatFrequenciaLabel,
  formatUnidadeMedidaText,
  formatUnidadeTempo,
} from './formatter-prescricao'
import { calculateDataFimTratamento } from './utils-prescricao'

export interface PrescricaoAtendimentoBodyModel {
  id: ID
  principioAtivo: string
  concentracao: string
  formaFarmaceutica: string
  tipoReceita: TipoReceitaEnum
  posologia: string
  viaAdministracao: ViaAdministracaoSelectModel
  recomendacoes: string
  dataInicioTratamento: LocalDate
  dataFimTratamento: LocalDate
  duracao: number
  escalaDuracao: UnidadeMedidaTempoEnum
  disponibilidade: DisponibilidadeHorusEnum
  usoContinuo: boolean
  motivoPrescricao: string
}

export function prescricaoAtendHeaderConverter(medicamentos: MedicamentoFormModel[]): string {
  return medicamentos
    ?.map((medicamento) => {
      const principioAtivo = medicamento.registroManual
        ? medicamento.principioAtivoText
        : medicamento.principioAtivoCombo.medicamento.principioAtivo
      const concentracao = medicamento.registroManual
        ? medicamento.concentracaoText
        : medicamento.principioAtivoCombo.medicamento.concentracao
      return `${principioAtivo} ${concentracao ?? ''}`
    })
    .join(' | ')
}

export function prescricaoAtendBodyConverter(medicamentos: MedicamentoFormModel[]): PrescricaoAtendimentoBodyModel[] {
  return medicamentos.map((medicamento) => {
    const principioAtivo: string = medicamento.registroManual
      ? medicamento.principioAtivoText
      : medicamento.principioAtivoCombo.principioAtivo.nome
    const concentracao: string = medicamento.registroManual
      ? medicamento.concentracaoText
      : medicamento.principioAtivoCombo.medicamento.concentracao
    const formaFarmaceutica: string = medicamento.registroManual
      ? medicamento.formaFarmaceutica.nome
      : medicamento.principioAtivoCombo.medicamento.formaFarmaceutica.nome
    const tipoReceita: TipoReceitaEnum = medicamento.registroManual
      ? medicamento.tipoReceita.tipoReceitaEnum
      : medicamento.principioAtivoCombo.principioAtivo.listaMaterial.tipoReceita
    return {
      id: medicamento.id,
      principioAtivo,
      concentracao,
      formaFarmaceutica,
      tipoReceita,
      posologia: medicamento.posologia,
      viaAdministracao: medicamento.viaAdministracao,
      recomendacoes: medicamento.recomendacoes,
      dataInicioTratamento: medicamento.dataInicioTratamento,
      dataFimTratamento: medicamento.dataFimTratamento,
      duracao: medicamento.duracao,
      escalaDuracao: medicamento.escalaDuracao,
      disponibilidade: medicamento.disponibilidade,
      usoContinuo: medicamento.usoContinuo,
      motivoPrescricao: formatCidOrCiap(medicamento.cidMotivoPrescricao, medicamento.ciapMotivoPrescricao),
    }
  })
}

export function convertReceitaMedicamentoToMedicamento(
  receita: ReceitaMedicamentoQueryModel,
  isVersaoAnterior: boolean,
  isReplicacao: boolean,
  dataInicioAtendimento: Instant,
  isRenovacao: boolean = false
): MedicamentoFormModel {
  const medicamento = receita.medicamento
  const medicamentoCatmat: MedicamentoCatmatSelectModel = { ...medicamento.medicamentoCatmat, medicamento }

  const localDateInicioTratamento = dateAsYyyyMmDd(toDate(dataInicioAtendimento))

  const dataFimTratamento = calculateDataFimTratamento(
    receita.quantidadeReceitada,
    localDateInicioTratamento,
    receita.medidaTempoTratamento
  )

  return {
    id: uuidv4(),
    editingId: isReplicacao ? null : receita.id,
    registroManual: receita.registroManual,
    manualMedicamentoId: isRenovacao && receita.registroManual ? receita.medicamento.id : null,
    principioAtivoCombo: !receita.registroManual && medicamentoCatmat,
    principioAtivoText: receita.registroManual && receita.medicamento.principioAtivo,
    concentracaoText: receita.registroManual && receita.medicamento.concentracao,
    viaAdministracao: receita.aplicacaoMedicamento,
    tipoReceita: tiposReceitaRecord[receita.tipoReceita],
    codigoPrescricaoDigital: receita.codigoPrescricaoDigital,
    formaFarmaceutica: receita.medicamento.formaFarmaceutica,
    tipoDose: tipoDose(receita),
    dose: isVersaoAnterior ? '' : receita.dose,
    qtDose: receita.qtDose?.toString().replace('.', ','),
    qtDoseManha: receita.qtDoseManha?.toString().replace('.', ','),
    qtDoseTarde: receita.qtDoseTarde?.toString().replace('.', ','),
    qtDoseNoite: receita.qtDoseNoite?.toString().replace('.', ','),
    unidadeMedidaDose: receita.unidadeMedidaDose,
    tipoFrequencia: receita.tipoFrequenciaDose ?? TipoFrequencia.INTERVALO,
    intervaloDose: receita.tipoFrequenciaDose === TipoFrequencia.INTERVALO ? parseNumber(receita.frequenciaDose) : null,
    frequenciaDose: frequenciaDose(receita, isVersaoAnterior),
    quantidadePeriodoFrequenciaTurno: receita.qtPeriodoFrequencia,
    unidadeMedidaTempoFrequenciaTurno: receita.medidaTempoFrequencia,
    turno: receita.tipoFrequenciaDose !== TipoFrequencia.TURNO ? null : turnoByStringRecord[receita.frequenciaDose],
    posologia: receita.posologia,
    dataInicioTratamento: localDateInicioTratamento,
    dataFimTratamento: dataFimTratamento ? dateAsYyyyMmDd(dataFimTratamento) : null,
    duracao: receita.usoContinuo ? null : receita.qtDuracaoTratamento,
    escalaDuracao: escalaDuracao(receita, isVersaoAnterior),
    quantidade: receita.quantidadeReceitada,
    usoContinuo: receita.usoContinuo,
    recomendacoes: receita.recomendacoes,
    unidadeFornecimento: receita.medicamento.unidadeFornecimento,
    isVersaoAnterior,
    receitaUsoContinuoGrupoId: receitaUsoContinuoGrupoId(receita, isRenovacao),
    tipoMotivoPrescricao: tipoMotivo(receita.cid10, receita.ciap),
    cidMotivoPrescricao: receita.cid10,
    ciapMotivoPrescricao: receita.ciap,
  } as MedicamentoFormModel
}

function tipoMotivo(cid?: CidSelectFieldModel, ciap?: CiapSelectFieldModel): TipoMotivoPrescricaoEnum | null {
  if (cid) {
    return TipoMotivoPrescricaoEnum.CID10
  }
  if (ciap) {
    return TipoMotivoPrescricaoEnum.CIAP2
  }
  return null
}

function tipoDose(receita: ReceitaMedicamentoQueryModel): TipoDoseEnum {
  if (receita.doseUnica) {
    return TipoDoseEnum.UNICA
  }
  if (receita.tipoFrequenciaDose === TipoFrequencia.FRACIONADO) {
    return TipoDoseEnum.FRACIONADA
  }
  return TipoDoseEnum.COMUM
}

function frequenciaDose(receita: ReceitaMedicamentoQueryModel, isVersaoAnterior: boolean): string | number | null {
  if (isVersaoAnterior) {
    return ''
  }
  if (receita.tipoFrequenciaDose === TipoFrequencia.FREQUENCIA) {
    return parseNumber(receita.frequenciaDose)
  }
  return null
}

function escalaDuracao(receita: ReceitaMedicamentoQueryModel, isVersaoAnterior: boolean): UnidadeMedidaTempoEnum {
  if (receita.usoContinuo) {
    return UnidadeMedidaTempoEnum.INDETERMINADO
  }
  return !isVersaoAnterior ? receita.medidaTempoTratamento : UnidadeMedidaTempoEnum.DIAS
}

function receitaUsoContinuoGrupoId(receita: ReceitaMedicamentoQueryModel, isRenovacao: boolean): ID | null {
  if (isRenovacao && receita.usoContinuo) {
    return receita?.receitaUsoContinuoGrupo?.id ?? receita.id
  }
  return null
}

export function convertMedicamentoToPrintDto(medicamento: MedicamentoFormModel): MedicamentoToPrintDto {
  return {
    id: medicamento.id,
    medicamentoId: !medicamento.registroManual ? medicamento.principioAtivoCombo.medicamento.id : '',
    principioAtivo: !medicamento.registroManual
      ? medicamento.principioAtivoCombo.principioAtivo.nome
      : medicamento.principioAtivoText,
    registroManual: medicamento.registroManual,
    categoria: medicamento?.principioAtivoCombo?.principioAtivo?.listaMaterial?.tipo,
    volume: !medicamento.registroManual ? medicamento.principioAtivoCombo.volume : '',
    concentracao: !medicamento.registroManual
      ? medicamento.principioAtivoCombo.medicamento.concentracao
      : !!medicamento.concentracaoText
      ? medicamento.concentracaoText
      : '',
    formaFarmaceutica: medicamento.registroManual
      ? medicamento.formaFarmaceutica.id
      : medicamento.principioAtivoCombo.medicamento.formaFarmaceutica.id,
    codigoCatMat: !medicamento.registroManual ? medicamento.principioAtivoCombo.codigoCatMat : '',
    codigoRename: !medicamento.registroManual ? medicamento.principioAtivoCombo.codigoRename : '',
    tipoReceita: medicamento.registroManual
      ? medicamento.tipoReceita.tipoReceitaEnum
      : medicamento.principioAtivoCombo.principioAtivo.listaMaterial.tipoReceita,
    unidadeFornecimento: !medicamento.registroManual ? medicamento.principioAtivoCombo.unidadeFornecimento.id : '',
    unidadeFornecimentoDescricao: medicamento.registroManual
      ? medicamento.unidadeFornecimento
      : medicamento.principioAtivoCombo.unidadeFornecimento.nome,
    disponibilidadeHorus: !medicamento.registroManual
      ? medicamento.disponibilidade
      : DisponibilidadeHorusEnum.DESABILITADO_MANUAL,
    quantidade: medicamento.quantidade,
    periodoTratamento: medicamento.escalaDuracao,
    duracaoTratamento: medicamento.duracao,
    tipoFrequencia: medicamento.tipoFrequencia,
    frequenciaDose: formatFrequenciaLabel({
      tipoFrequencia: medicamento.tipoFrequencia,
      intervaloDose: medicamento.intervaloDose,
      frequenciaDose: medicamento.frequenciaDose,
      quantidadePeriodoFrequencia: medicamento.quantidadePeriodoFrequenciaTurno,
      unidadeMedidaTempoFrequencia: medicamento.unidadeMedidaTempoFrequenciaTurno,
      turno: medicamento.turno,
      doseUnica: medicamento.tipoDose === TipoDoseEnum.UNICA,
    }),
    recomendacoes: medicamento.recomendacoes,
    posologia: createPosologia(medicamento),
    dataInicio: medicamento.dataInicioTratamento,
    dataConclusao: medicamento.dataFimTratamento,
    unidadeMedidaDuracao: medicamento.escalaDuracao,
    usoContinuo: medicamento.usoContinuo,
    doseUnica: medicamento.tipoDose === TipoDoseEnum.UNICA,
    dose: formatDoseReceitaLabel(medicamento.unidadeMedidaDose, medicamento.qtDose, null),
    viaAdministracao: medicamento.viaAdministracao.nome,
  } as MedicamentoToPrintDto
}

export function processTextoMedicamentos(
  medicamentosPrescricao: MedicamentoFormModel[],
  codigoAcessoPrescricao: string
): string {
  return medicamentosPrescricao
    .filter((medicamento) => medicamento.codigoPrescricaoDigital === codigoAcessoPrescricao)
    .map((medicamento) => {
      const principioAtivo: string = medicamento.registroManual
        ? medicamento.principioAtivoText
        : medicamento.principioAtivoCombo.principioAtivo.nome
      const concentracao: string = medicamento.registroManual
        ? medicamento.concentracaoText
        : medicamento.principioAtivoCombo.medicamento.concentracao
      return `${principioAtivo} ${concentracao ?? ''}`
    })
    .join(' | ')
}

export function prescricaoDigitalTableConverter(
  prescricaoDigital: PrescricaoDigitalFormModel
): PrescricaoDigitalTableModel[] {
  return Object.entries(prescricaoDigital).map(([codigoAcesso, prescricao]) => ({
    codigoPrescricaoDigital: codigoAcesso,
    textoMedicamentos: prescricao.textoMedicamentos,
    prescricaoInvalidada: prescricao.prescricaoInvalidada,
    emailEnvio: prescricao.emailEnvio,
    dataAssinatura: prescricao.dataAssinatura,
  }))
}

export function convertReceitaMedicamentosToPrescriocoesDigitais(
  receita: ReceitaMedicamentoQueryModel[],
  dataReferencia: Instant,
  emailCidadao: string
): PrescricaoDigitalFormModel {
  const filteredReceita: ReceitaMedicamentoQueryModel[] = receita?.filter(
    (item) => item.codigoPrescricaoDigital != null
  )

  if (isEmpty(filteredReceita)) {
    return {}
  }

  const prescricao: PrescricaoDigitalFormModel = {}
  const medicamentos: MedicamentoFormModel[] = filteredReceita.map((receita) =>
    convertReceitaMedicamentoToMedicamento(receita, false, false, dataReferencia)
  )

  filteredReceita.forEach((receita) => {
    const codigoPrescricaoDigital = receita.codigoPrescricaoDigital

    if (!prescricao[codigoPrescricaoDigital]) {
      prescricao[codigoPrescricaoDigital] = {
        prescricaoInvalidada: false,
        emailEnvio: emailCidadao,
        dataAssinatura: instantToYyyyMmDd(dataReferencia),
        textoMedicamentos: processTextoMedicamentos(medicamentos, codigoPrescricaoDigital),
      }
    }
  })

  return prescricao
}

function createPosologia(medicamento: MedicamentoFormModel): string {
  if (TipoDoseEnum.FRACIONADA === medicamento.tipoDose) {
    const doseText: string = medicamento.qtDose
      ? `${medicamento.qtDose} ${formatUnidadeMedidaText(medicamento.qtDose, medicamento.unidadeMedidaDose)}`
      : ''
    const doseManhaText: string = medicamento.qtDoseManha
      ? `<br>&nbsp; ${medicamento.qtDoseManha} ${formatUnidadeMedidaText(
          medicamento.qtDoseManha,
          medicamento.unidadeMedidaDose
        )} pela manhã,`
      : ''
    const doseTardeText: string = medicamento.qtDoseTarde
      ? `<br>&nbsp; ${medicamento.qtDoseTarde} ${formatUnidadeMedidaText(
          medicamento.qtDoseTarde,
          medicamento.unidadeMedidaDose
        )} pela tarde,`
      : ''
    const doseNoiteText: string = medicamento.qtDoseNoite
      ? `<br>&nbsp; ${medicamento.qtDoseNoite} ${formatUnidadeMedidaText(
          medicamento.qtDoseNoite,
          medicamento.unidadeMedidaDose
        )} pela noite,`
      : ''
    const periodoFrequenciaText: string =
      medicamento.quantidadePeriodoFrequenciaTurno && medicamento.unidadeMedidaTempoFrequenciaTurno
        ? `${formatUnidadeTempo(
            parseNumber(medicamento.quantidadePeriodoFrequenciaTurno),
            medicamento.unidadeMedidaTempoFrequenciaTurno,
            medicamento.tipoFrequencia
          )}`
        : ''

    const posologiaText: string = `${doseText} ${periodoFrequenciaText}. Sendo:
    ${doseManhaText} ${doseTardeText} ${doseNoiteText}`.replaceLastOccurrence(',', '.')

    return posologiaText
  } else {
    return medicamento.posologia
  }
}

export function resolvePrincipioAtivo(medicamento) {
  return medicamento.registroManual
    ? medicamento.principioAtivoText
    : medicamento.principioAtivoCombo.principioAtivo.nome
}

export function resolveConcentracao(medicamento) {
  return !medicamento.registroManual
    ? medicamento.principioAtivoCombo.medicamento.concentracao
    : medicamento.concentracaoText
    ? medicamento.concentracaoText
    : ''
}

export function resolveTipoReceita(medicamento: MedicamentoFormModel): TipoReceitaEnum {
  return medicamento.registroManual
    ? medicamento.tipoReceita.tipoReceitaEnum
    : medicamento.principioAtivoCombo.principioAtivo.listaMaterial.tipoReceita
}

export function resolveCategoria(medicamento: MedicamentoFormModel): ListaMedicamentoEnum | undefined {
  return medicamento.registroManual
    ? undefined
    : (medicamento.principioAtivoCombo?.principioAtivo?.listaMaterial?.tipo as ListaMedicamentoEnum)
}

export function resolveMedicamento(medicamento) {
  const principioAtivo = resolvePrincipioAtivo(medicamento)
  const concentracao = resolveConcentracao(medicamento)
  const tipoReceita = resolveTipoReceita(medicamento)
  const categoria = resolveCategoria(medicamento)

  return { principioAtivo, concentracao, tipoReceita, categoria }
}

export const filtrarMedicamentos = (
  medicamentos: MedicamentoFormModel[],
  categoriasNaoPermitidas: ListaMedicamentoEnum[],
  tiposReceitaNaoPermitidos: TipoReceitaEnum[]
) => {
  return medicamentos.reduce(
    (acc, medicamento) => {
      const isMedicamentoPermitido = medicamento.registroManual
        ? !tiposReceitaNaoPermitidos?.includes(medicamento.tipoReceita.tipoReceitaEnum)
        : !categoriasNaoPermitidas?.includes(
            medicamento?.principioAtivoCombo?.principioAtivo?.listaMaterial?.tipo as ListaMedicamentoEnum
          )

      if (isMedicamentoPermitido) {
        acc.medicamentosPermitidos.push(medicamento)
      } else {
        acc.medicamentosNaoPermitidos.push(medicamento)
      }
      return acc
    },
    { medicamentosPermitidos: [], medicamentosNaoPermitidos: [] }
  )
}
